/**
 * AI Synergy Bridge - Dashboard Service
 * Real-time dashboard data management with WebSocket integration
 */

import { toast } from 'sonner'

export interface DashboardMetrics {
  portfolioValue: string
  totalProperties: number
  avgCapRate: string
  occupancyRate: string
  noiGrowth: string
  riskScore: string
  opportunities: number
  quantumSecurity: string
  esgCompliance: string
}

export interface MarketAlert {
  id: string
  type: string
  title: string
  description: string
  priority: 'low' | 'medium' | 'high' | 'critical'
  timestamp: string
  location?: string
  impact?: string
}

export interface RecentActivity {
  id: string
  type: string
  description: string
  timestamp: string
  amount?: string
  status: string
}

export interface PortfolioData {
  totalValue: number
  todaysPnL: number
  activePositions: number
  assetAllocation: Array<{
    name: string
    value: number
    percentage: number
    change: number
  }>
}

export interface MarketTrend {
  symbol: string
  name: string
  price: number
  change: number
  changePercent: number
  volume: number
  marketCap: number
}

export interface AiInsight {
  metric: string
  value: number
  target: number
  confidence?: number
  prediction?: string
  trend?: 'up' | 'down' | 'stable'
}

export interface RealtimeData {
  timestamp: string
  metrics: DashboardMetrics
  alerts: MarketAlert[]
  activity: RecentActivity[]
  portfolio: PortfolioData
  trends: MarketTrend[]
  insights: AiInsight[]
}

export interface WebSocketMessage {
  type: 'metrics' | 'alert' | 'activity' | 'portfolio' | 'trends' | 'insights' | 'ping' | 'pong'
  data?: any
  timestamp: string
}

class DashboardService {
  private static instance: DashboardService
  private baseUrl: string
  private websocket: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private listeners: Map<string, Set<(data: any) => void>> = new Map()
  private isConnected = false
  private heartbeatInterval: NodeJS.Timeout | null = null

  constructor() {
    this.baseUrl = process.env.NEXT_PUBLIC_API_URL || 'https://aisynergybridge.com'
  }

  static getInstance(): DashboardService {
    if (!DashboardService.instance) {
      DashboardService.instance = new DashboardService()
    }
    return DashboardService.instance
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('access_token')
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    }
  }

  // WebSocket Management
  connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const wsBase = (this.baseUrl || 'https://aisynergybridge.com').replace(/^http/, 'ws')
        const wsUrl = `${wsBase}/api/v1/ws/dashboard`
        this.websocket = new WebSocket(wsUrl)

        this.websocket.onopen = () => {
          console.log('Dashboard WebSocket connected')
          this.isConnected = true
          this.reconnectAttempts = 0
          this.startHeartbeat()
          resolve()
        }

        this.websocket.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data)
            this.handleWebSocketMessage(message)
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }

        this.websocket.onclose = () => {
          console.log('Dashboard WebSocket disconnected')
          this.isConnected = false
          this.stopHeartbeat()
          this.attemptReconnect()
        }

        this.websocket.onerror = (error) => {
          console.error('Dashboard WebSocket error:', error)
          reject(error)
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  private handleWebSocketMessage(message: WebSocketMessage) {
    const listeners = this.listeners.get(message.type)
    if (listeners) {
      listeners.forEach(callback => callback(message.data))
    }

    // Handle specific message types
    switch (message.type) {
      case 'alert':
        if (message.data.priority === 'critical') {
          toast.error(`Critical Alert: ${message.data.title}`)
        } else if (message.data.priority === 'high') {
          toast.warning(`Alert: ${message.data.title}`)
        }
        break
      case 'pong':
        // Heartbeat response received
        break
    }
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.websocket?.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({
          type: 'ping',
          timestamp: new Date().toISOString()
        }))
      }
    }, 30000) // Send ping every 30 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)
      
      setTimeout(() => {
        console.log(`Attempting to reconnect WebSocket (attempt ${this.reconnectAttempts})`)
        this.connectWebSocket().catch(console.error)
      }, delay)
    }
  }

  // Event Listeners
  addEventListener(type: string, callback: (data: any) => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }
    this.listeners.get(type)!.add(callback)
  }

  removeEventListener(type: string, callback: (data: any) => void) {
    const listeners = this.listeners.get(type)
    if (listeners) {
      listeners.delete(callback)
    }
  }

  // API Methods
  async getMetrics(timeframe: string = '30d'): Promise<DashboardMetrics> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/dashboard/metrics?timeframe=${timeframe}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error)
      throw error
    }
  }

  async getAlerts(): Promise<MarketAlert[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/dashboard/alerts`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching dashboard alerts:', error)
      throw error
    }
  }

  async getActivity(): Promise<RecentActivity[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/dashboard/activity`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching dashboard activity:', error)
      throw error
    }
  }

  async getPortfolioData(timeframe: string = '30d'): Promise<PortfolioData> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/dashboard/portfolio-data?timeframe=${timeframe}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching portfolio data:', error)
      throw error
    }
  }

  async getMarketTrends(timeframe: string = '30d'): Promise<MarketTrend[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/dashboard/market-trends?timeframe=${timeframe}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching market trends:', error)
      throw error
    }
  }

  async getAiInsights(): Promise<AiInsight[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/dashboard/ai-insights`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching AI insights:', error)
      throw error
    }
  }

  async generateReport(timeframe: string = '30d'): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/dashboard/generate-report`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ timeframe })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error generating report:', error)
      throw error
    }
  }

  // Real-time data fetching with parallel requests
  async getAllDashboardData(timeframe: string = '30d'): Promise<RealtimeData> {
    try {
      const [metrics, alerts, activity, portfolio, trends, insights] = await Promise.allSettled([
        this.getMetrics(timeframe),
        this.getAlerts(),
        this.getActivity(),
        this.getPortfolioData(timeframe),
        this.getMarketTrends(timeframe),
        this.getAiInsights()
      ])

      return {
        timestamp: new Date().toISOString(),
        metrics: metrics.status === 'fulfilled' ? metrics.value : this.getDefaultMetrics(),
        alerts: alerts.status === 'fulfilled' ? alerts.value : [],
        activity: activity.status === 'fulfilled' ? activity.value : [],
        portfolio: portfolio.status === 'fulfilled' ? portfolio.value : this.getDefaultPortfolio(),
        trends: trends.status === 'fulfilled' ? trends.value : [],
        insights: insights.status === 'fulfilled' ? insights.value : []
      }
    } catch (error) {
      console.error('Error fetching all dashboard data:', error)
      throw error
    }
  }

  // Fallback data methods
  private getDefaultMetrics(): DashboardMetrics {
    return {
      portfolioValue: '$2,400,000,000',
      totalProperties: 1247,
      avgCapRate: '6.2%',
      occupancyRate: '94.2%',
      noiGrowth: '+15.7%',
      riskScore: '7.2/10',
      opportunities: 47,
      quantumSecurity: '99.8%',
      esgCompliance: '92.5%'
    }
  }

  private getDefaultPortfolio(): PortfolioData {
    return {
      totalValue: 2400000000,
      todaysPnL: 15750000,
      activePositions: 1247,
      assetAllocation: [
        { name: 'Commercial', value: 1200000000, percentage: 50, change: 2.3 },
        { name: 'Residential', value: 720000000, percentage: 30, change: 1.8 },
        { name: 'Industrial', value: 360000000, percentage: 15, change: 3.1 },
        { name: 'Retail', value: 120000000, percentage: 5, change: -0.5 }
      ]
    }
  }

  // Connection status
  isWebSocketConnected(): boolean {
    return this.isConnected && this.websocket?.readyState === WebSocket.OPEN
  }

  // Cleanup
  disconnect() {
    if (this.websocket) {
      this.websocket.close()
      this.websocket = null
    }
    this.stopHeartbeat()
    this.isConnected = false
  }
}

export default DashboardService.getInstance()